import {
    createFileRoute,
    redirect,
    stripSearchParams,
} from '@tanstack/react-router';
import { defaultStatsParams, statsParamsSchema } from './-SearchParams';
import { ScrollArea } from '~/primitives/scroll-area';
import TabSwitch, { ListTabSwitch } from '~/partials/TabSwitch';
import {
    moderatorLeaderboardStatsQuery,
    recordLevelStatsQuery,
    submissionDailyStatsQuery,
    submissionLevelStatsQuery,
} from '~/util/queries/staff/stats';
import { SubmissionsStats } from './-Submissions/SubmissionsStats';
import { RecordsStats } from './-Records/RecordsStats';
import { ModStatsLeaderboard } from './-Leaderboard/ModStatsLeaderboard';
import { DailyStats } from './-Daily/DailyStats';
import { hasScope } from '~/auth/utils';

export const Route = createFileRoute('/_authenticated/staff/stats/')({
    validateSearch: statsParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultStatsParams)],
    },
    loaderDeps: ({
        search: {
            list,
            tab,
            only_active,
            include_hidden_reviewers,
            page,
            per_page,
            reviewer,
            since,
            until,
        },
    }) => ({
        list,
        tab,
        only_active,
        include_hidden_reviewers,
        page,
        per_page,
        reviewer,
        since,
        until,
    }),
    beforeLoad: ({ context: { auth } }) => {
        if (!hasScope(auth, 'submission_see_other_reviewer_statistics')) {
            throw redirect({
                to: '/staff/stats/$userid',
                replace: true,
                params: { userid: auth?.id ?? '' },
            });
        }
    },
    loader: async ({
        deps: {
            list,
            reviewer,
            page,
            per_page,
            only_active,
            include_hidden_reviewers,
            since,
            until,
        },
        context: { queryClient },
    }) => {
        const [daily, leaderboard, records, submissions] = await Promise.all([
            queryClient.fetchQuery(
                submissionDailyStatsQuery({ list, reviewer, page, per_page }),
            ),
            queryClient.fetchQuery(
                moderatorLeaderboardStatsQuery({
                    since,
                    until,
                    only_active,
                    include_hidden_reviewers,
                    list,
                }),
            ),
            queryClient.fetchQuery(recordLevelStatsQuery({ list })),
            queryClient.fetchQuery(submissionLevelStatsQuery({ list })),
        ]);
        return { daily, leaderboard, records, submissions };
    },
    component: Stats,
});

function Stats() {
    const tab = Route.useSearch({ select: ({ tab }) => tab });
    const navigate = Route.useNavigate();
    return (
        <main className="h-full flex justify-center">
            <div className="flex flex-col items-center gap-4 w-full h-full bg-background-dark rounded-2xl py-4">
                <ScrollArea className="w-full p-4 lg:h-[calc(100vh-150px)]">
                    <div className="flex w-full flex-col lg:flex-row lg:justify-between">
                        <h1 className="text-secondary-foreground">
                            Statistics
                        </h1>

                        <div className="flex w-full flex-col lg:flex-row lg:justify-between">
                            <ListTabSwitch from="/staff/stats" />
                            <TabSwitch
                                current={tab}
                                onChange={(value) =>
                                    navigate({
                                        search: (prev) => ({
                                            list: prev.list,
                                            tab: value,
                                        }),
                                    })
                                }
                                options={[
                                    'submissions',
                                    'records',
                                    'daily',
                                    'leaderboard',
                                ]}
                                optionsDisplay={[
                                    'Current Queue',
                                    'All records',
                                    'Per-day stats',
                                    'Mod Leaderboard',
                                ]}
                                size="md"
                            />
                        </div>
                    </div>

                    {tab === 'submissions' && <SubmissionsStats />}
                    {tab === 'records' && <RecordsStats />}
                    {tab === 'daily' && <DailyStats />}
                    {tab === 'leaderboard' && <ModStatsLeaderboard />}
                </ScrollArea>
            </div>
        </main>
    );
}
